import { useEffect } from 'react';
import { motion } from 'framer-motion';
import styles from './DayBook.module.css';
import { useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Divider from '@mui/material/Divider';

import {
    Button,
    Box,
    TextField,
    MenuItem,
    Menu,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Chip
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { dayBookApprovalApi, dayBookListApi } from 'utils/API/SuperdistributorEmployee APIs/SuperdistributorApiCalls';
import { globalNotifyError, globalNotifySuccess } from 'views/utilities/AlertToast';

const MyselfApproval = () => {
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [status, setStatus] = useState('pending');
    const [transactions, setTransactions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0); // from API
    const [loading, setLoading] = useState(false);
    const [actionAnchorEl, setActionAnchorEl] = useState({});
    const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
    const [remarkEntries, setRemarkEntries] = useState([]); // will hold parsed note objects
    const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
    const [approvalStatus, setApprovalStatus] = useState('');
    const [approvalNote, setApprovalNote] = useState('');
    const [selectedTxnId, setSelectedTxnId] = useState(null);

    const fetchTransactions = async (page = currentPage, size = pageSize) => {
        setLoading(true);
        try {
            const payload = {
                fromDate: fromDate.toISOString().split('T')[0],
                toDate: toDate ? toDate.toISOString().split('T')[0] : '',
                pageSize: size.toString(),
                pageNumber: page.toString(),
                status: status, // pending, approved, rejected, all
                is_pending_approval: false,
                is_myself_approval: true
            };

            const res = await dayBookListApi(payload);

            setTransactions(res.data?.data || []);
            setTotalCount(res.data?.count || 0); // <- Add this line
        } catch (err) {
            console.error('Error fetching transactions', err);
        } finally {
            setLoading(false); // Set loading to false when fetch completes (success or error)
        }
    };
    useEffect(() => {
        fetchTransactions(currentPage, pageSize);
        // eslint-disable-next-line
    }, [currentPage, pageSize, status]);
    const handleRowsPerPageChange = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setCurrentPage(1); // Reset to the first page when rows per page changes
    };
    const handlePageChange = (event, newPage) => {
        // TablePagination is 0-indexed, so we add 1 for our 1-indexed state
        setCurrentPage(newPage + 1);
    };
    const handleApprovalSubmit = async () => {
        try {
            await dayBookApprovalApi({
                id: selectedTxnId,
                status: approvalStatus,
                note: approvalNote
            });
            // update row locally
            setTransactions((txns) => txns.map((tx) => (tx.eid === selectedTxnId ? { ...tx, status: approvalStatus } : tx)));
            globalNotifySuccess(`Successfully ${approvalStatus}!`);
            fetchTransactions();
        } catch (err) {
            globalNotifyError('Error submitting approval');
        } finally {
            setApprovalDialogOpen(false);
        }
    };

    const handleActionClick = (e, eid) => {
        setActionAnchorEl((prev) => ({ ...prev, [eid]: e.currentTarget }));
    };
    // close the menu
    const handleCloseAction = (eid) => {
        setActionAnchorEl((prev) => ({ ...prev, [eid]: null }));
    };

    // user clicked Approve in menu
    const handleApproveClick = (eid) => {
        setSelectedTxnId(eid);
        setApprovalStatus('approved');
        setApprovalNote('');
        setApprovalDialogOpen(true);
        handleCloseAction(eid);
    };
    // user clicked Reject in menu
    const handleRejectClick = (eid) => {
        setSelectedTxnId(eid);
        setApprovalStatus('rejected');
        setApprovalNote('');
        setApprovalDialogOpen(true);
        handleCloseAction(eid);
    };
    const handleViewRemark = (tx) => {
        let entries = [];
        try {
            const parsed = JSON.parse(tx.note);

            entries = Array.isArray(parsed) ? parsed : [parsed];
        } catch (e) {
            console.error('Failed to parse note JSON', e);
        }
        setRemarkEntries(entries);
        setRemarkDialogOpen(true);
    };

    return (
        <div className={styles.container}>
            <Box sx={{ padding: 2 }}>
                {/* First Section: Day Book & Pending Approvals */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between', // space between left and right groups
                        alignItems: { xs: 'stretch', sm: 'center' },
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 3
                    }}
                >
                    {/* Left Side: Day Book & Pending Approvals */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="contained" disableElevation className={styles.daybookbtn}>
                            My Approval
                        </Button>
                    </Box>
                </Box>

                {/* Second Section: Date Picker + Submit */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { sm: 'center' },
                        gap: 2,
                        mb: 3
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Box display="flex" gap={2}>
                            <DatePicker
                                label="From Date"
                                value={fromDate}
                                onChange={(newValue) => setFromDate(newValue)}
                                maxDate={new Date()}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        variant: 'outlined',
                                        sx: { width: '180px' }
                                    }
                                }}
                            />
                            <DatePicker
                                label="To Date"
                                value={toDate}
                                onChange={(newValue) => setToDate(newValue)}
                                minDate={fromDate}
                                maxDate={new Date()}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        variant: 'outlined',
                                        sx: { width: '180px' }
                                    }
                                }}
                            />
                        </Box>
                    </LocalizationProvider>

                    <TextField
                        label="Status"
                        select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ width: '180px' }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>

                        <MenuItem value="rejected">Rejected</MenuItem>
                    </TextField>

                    <LoadingButton onClick={() => fetchTransactions(1, pageSize)} className={styles.addClientButton} loading={loading}>
                        Submit
                    </LoadingButton>
                </Box>

                {/* ...Your table and remaining content... */}
            </Box>

            <motion.div
                className={styles.tableSection}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
            >
                <Box className={styles.tabContent}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Sl. No.</th>
                                <th>Date</th>
                                <th>Particulars</th>

                                <th>Cash in</th>
                                <th>Cash out</th>
                                <th>Denominations</th>
                                <th>Status</th>
                                <th>Action</th>
                                <th>Remark</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(transactions) && transactions.length > 0 ? (
                                transactions.map((tx, index) => (
                                    <tr key={index}>
                                        <td>{(currentPage - 1) * pageSize + index + 1}</td>
                                        <td>{tx.txn_date}</td>
                                        <td>
                                            {tx.from_entity_name || tx.from || 'myself'}
                                            <br />
                                            To: {tx.to_entity_name || tx.to || 'myself'}
                                        </td>

                                        <td>₹{tx.cash_in}</td>
                                        <td>
                                            ₹{tx.cash_out}
                                            {parseFloat(tx.premium_charge) > 0 && (
                                                <span style={{ fontSize: '14px', color: '#888' }}> (+₹{tx.premium_charge} premium)</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className={styles.denominationGrid}>
                                                {tx.denominations &&
                                                    Object.entries(JSON.parse(tx.denominations)).map(([note, qty], i) => (
                                                        <div
                                                            key={i}
                                                            style={{
                                                                fontSize: '14px',
                                                                color: Number(qty) === 0 ? 'red' : 'inherit'
                                                            }}
                                                        >
                                                            {note} × {qty}
                                                        </div>
                                                    ))}
                                            </div>
                                        </td>
                                        <td>
                                            <Chip
                                                label={tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                                                size="small"
                                                color={tx.status === 'approved' ? 'success' : 'error'}
                                            />
                                        </td>

                                        <td>
                                            {tx.status !== 'approved' && tx.status !== 'rejected' && (
                                                <>
                                                    {/* Action Dropdown */}
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => handleActionClick(e, tx.eid)}
                                                        className={styles.daybookbtn}
                                                    >
                                                        Action
                                                    </Button>
                                                    <Menu
                                                        anchorEl={actionAnchorEl[tx.eid]}
                                                        open={Boolean(actionAnchorEl[tx.eid])}
                                                        onClose={() => handleCloseAction(tx.eid)}
                                                    >
                                                        <MenuItem onClick={() => handleApproveClick(tx.eid)}>Approve</MenuItem>
                                                        <MenuItem onClick={() => handleRejectClick(tx.eid)}>Reject</MenuItem>
                                                    </Menu>
                                                </>
                                            )}
                                        </td>

                                        <td>
                                            {/* Remark View Button */}
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => handleViewRemark(tx)}
                                                style={{ color: '#388e3c' }}
                                            >
                                                View
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                                        No transactions found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    <Dialog open={remarkDialogOpen} onClose={() => setRemarkDialogOpen(false)}>
                        <DialogTitle>Remarks</DialogTitle>
                        <DialogContent dividers>
                            {remarkEntries.length > 0 ? (
                                remarkEntries.map((entry, idx) => (
                                    <Box key={idx} mb={2}>
                                        <Typography>
                                            <strong>Message:</strong> {entry.message}
                                        </Typography>
                                        <Typography>
                                            <strong>Status:</strong> {entry.status}
                                        </Typography>
                                        <Typography>
                                            <strong>Date:</strong> {entry.date}
                                        </Typography>
                                        {idx < remarkEntries.length - 1 && <Divider sx={{ my: 1 }} />}
                                    </Box>
                                ))
                            ) : (
                                <Typography>No remarks available.</Typography>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setRemarkDialogOpen(false)} style={{ color: '#388e3c' }}>
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)}>
                        <DialogTitle>{approvalStatus === 'approved' ? 'Approve' : 'Reject'} Entry</DialogTitle>
                        <DialogContent dividers>
                            <TextField
                                label="Note"
                                fullWidth
                                multiline
                                minRows={2}
                                value={approvalNote}
                                onChange={(e) => setApprovalNote(e.target.value)}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setApprovalDialogOpen(false)} style={{ color: '#388e3c' }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleApprovalSubmit}
                                disabled={!approvalNote.trim()}
                                className={styles.addClientButton}
                            >
                                Submit
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={pageSize}
                        page={currentPage - 1} // TablePagination is 0-indexed
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        sx={{
                            marginTop: 2,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            borderTop: '1px solid #e0e0e0',
                            paddingTop: '8px'
                        }}
                    />
                </Box>
            </motion.div>
        </div>
    );
};

export default MyselfApproval;
